import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const MobileCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const kt = searchParams.get("kt") || "";
  const failCode = searchParams.get("code") || "";
  const failMessage = searchParams.get("message") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [shopName, setShopName] = useState("");
  const [items, setItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [readyForPayment, setReadyForPayment] = useState(false);
  const [ordername, setOrdername] = useState("");
  const [orderId, setOrderId] = useState("");
  const [tossClientKey, setTossClientKey] = useState("");

  const widgetRef = useRef(null);
  const widgetRenderedRef = useRef(false);

  const getImageSrc = useCallback((imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
    if (imagePath.startsWith("/static/")) return imagePath;
    if (imagePath.startsWith("static/")) return `/${imagePath}`;
    if (imagePath.startsWith("/")) return `/static${imagePath}`;
    return `/static/${imagePath}`;
  }, []);

  const formatPrice = useCallback((value) => {
    return `${Number(value || 0).toLocaleString("ko-KR")}원`;
  }, []);

  const fetchCheckout = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get("/api/kiosk/mobile_checkout", {
        withCredentials: true,
      });

      const data = res.data || {};

      if (!data.ok) {
        setErrorMsg(data.message || "결제 정보를 불러오지 못했습니다.");
        return;
      }

      setShopName(data.shop_name || "");
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalPrice(Number(data.total_price || 0));
      setBuyerName(data.buyer_name || "");
      setBuyerPhone(data.buyer_phone || "");
      setReadyForPayment(Boolean(data.ready_for_payment));
      setErrorMsg(data.error_msg || "");
      setOrdername(data.ordername || "");
      setOrderId(data.order_id || "");
      setTossClientKey(data.toss_client_key || "");
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "결제 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCheckout();
  }, [fetchCheckout]);

  useEffect(() => {
    const existing = document.querySelector('script[data-toss-widget="1"]');
    if (existing) return;

    const script = document.createElement("script");
    script.src = "https://js.tosspayments.com/v1/payment-widget";
    script.async = true;
    script.dataset.tossWidget = "1";
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!readyForPayment) {
      widgetRef.current = null;
      widgetRenderedRef.current = false;
      return;
    }

    if (!tossClientKey || !window.PaymentWidget) return;
    if (widgetRenderedRef.current) return;

    const target = document.getElementById("payment-method");
    if (!target) return;

    target.innerHTML = "";

    const paymentWidget = window.PaymentWidget(
      tossClientKey,
      window.PaymentWidget.ANONYMOUS
    );

    paymentWidget.renderPaymentMethods("#payment-method", Number(totalPrice || 0));

    widgetRef.current = paymentWidget;
    widgetRenderedRef.current = true;
  }, [readyForPayment, tossClientKey, totalPrice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setErrorMsg("");

    try {
      const res = await axios.post(
        "/api/kiosk/mobile_checkout",
        {
          buyer_name: buyerName,
          buyer_phone: buyerPhone,
        },
        {
          withCredentials: true,
        }
      );

      const data = res.data || {};

      if (!data.ok) {
        setErrorMsg(data.message || "주문 생성에 실패했습니다.");
        return;
      }

      setShopName(data.shop_name || "");
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalPrice(Number(data.total_price || 0));
      setBuyerName(data.buyer_name || "");
      setBuyerPhone(data.buyer_phone || "");
      setReadyForPayment(Boolean(data.ready_for_payment));
      setErrorMsg(data.error_msg || "");
      setOrdername(data.ordername || "");
      setOrderId(data.order_id || "");
      setTossClientKey(data.toss_client_key || "");

      if (!data.ready_for_payment && data.error_msg) {
        setErrorMsg(data.error_msg);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "주문 생성에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const generateRandomString = () => {
    return window.btoa(Math.random().toString()).replace(/=/g, "").slice(0, 20);
  };

  const requestPayment = async () => {
    if (!widgetRef.current) {
      alert("결제 위젯이 아직 준비되지 않았습니다.");
      return;
    }

    const successUrl =
      `${window.location.origin}/kiosk/mobile/success` +
      `?order_id=${encodeURIComponent(orderId)}` +
      `&kt=${encodeURIComponent(kt)}`;

    const failUrl =
      `${window.location.origin}/kiosk/mobile/checkout` +
      `?kt=${encodeURIComponent(kt)}`;

    try {
      await widgetRef.current.requestPayment({
        orderId: generateRandomString(),
        orderName: ordername,
        successUrl,
        failUrl,
      });
    } catch (err) {
      console.error("결제 요청 실패:", err);
    }
  };

  return (
    <>
      <style>{`
        body{ background:#f6f7f9; }
        .card{ border:0; border-radius:14px; }
        .thumb{ width:72px; height:72px; object-fit:cover; border-radius:10px; background:#eee; }
        .sticky-bar{ position:sticky; bottom:0; z-index:10; }
        .item-row{ padding:12px; }
        .muted{ color:#6c757d; }
      `}</style>

      <div className="container py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="text-muted small">{shopName}</div>
            <h4 className="fw-bold m-0">결제하기</h4>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(`/kiosk/mobile/cart?kt=${encodeURIComponent(kt)}`)}
          >
            장바구니
          </button>
        </div>

        {failMessage ? (
          <div className="alert alert-danger">
            {decodeURIComponent(failMessage)}
            {failCode ? <div className="small mt-1">{decodeURIComponent(failCode)}</div> : null}
          </div>
        ) : null}

        {errorMsg ? <div className="alert alert-danger">{errorMsg}</div> : null}

        {loading ? (
          <div className="card p-3">불러오는 중...</div>
        ) : !readyForPayment ? (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <div className="fw-bold mb-2">주문 상품</div>

                {items.map((it, idx) => {
                  const options = Array.isArray(it.options) ? it.options : [];

                  const originalPrice = Number(
                    it.original_price ??
                    it.normal_price ??
                    it.price ??
                    0
                  );

                  const displayPrice = Number(
                    it.display_price ??
                    it.product_price ??
                    it.price ??
                    0
                  );

                  const optionTotal = Number(
                    it.option_total ??
                    it.option_price ??
                    0
                  );

                  const unitPrice = Number(
                    it.unit_price ??
                    it.price ??
                    (displayPrice + optionTotal)
                  );

                  const quantity = Number(
                    it.quantity ??
                    it.qty ??
                    1
                  );

                  const resultPrice = Number(
                    it.result_price ??
                    it.subtotal ??
                    it.line ??
                    it.line_total ??
                    (unitPrice * quantity)
                  );

                  const isSale =
                    Boolean(it.is_sale) ||
                    (
                      originalPrice > 0 &&
                      displayPrice > 0 &&
                      displayPrice < originalPrice
                    );

                  return (
                    <div
                      className={`d-flex gap-3 align-items-center item-row ${
                        idx > 0 ? "border-top" : ""
                      }`}
                      key={`${it.cart_id || it.product_id}-${idx}`}
                    >
                      {it.image_path ? (
                        <img
                          src={getImageSrc(it.image_path)}
                          className="thumb"
                          alt=""
                        />
                      ) : (
                        <div className="thumb" />
                      )}

                      <div className="flex-grow-1">
                        <div className="fw-bold">{it.name}</div>

                        {options.length > 0 ? (
                          <div className="mt-1">
                            {options.map((opt, optIdx) => {
                              const groupName =
                                opt.option_group_name ||
                                opt.group_name ||
                                "";

                              const itemName =
                                opt.option_item_name ||
                                opt.item_name ||
                                "";

                              const optionPrice = Number(
                                opt.option_price ??
                                opt.extra_price ??
                                0
                              );

                              return (
                                <div
                                  className="text-muted small"
                                  key={`${it.cart_id || it.product_id}-${optIdx}`}
                                >
                                  · {groupName ? `${groupName}: ` : ""}
                                  {itemName}
                                  {optionPrice !== 0
                                    ? ` (${optionPrice > 0 ? "+" : ""}${formatPrice(optionPrice)})`
                                    : ""}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}

                        <div className="mt-1">
                          {isSale ? (
                            <div
                              className="text-muted small"
                              style={{ textDecoration: "line-through" }}
                            >
                              정상가 {formatPrice(originalPrice)}
                            </div>
                          ) : null}

                          <div className={isSale ? "fw-bold text-danger" : "text-muted small"}>
                            상품 {formatPrice(displayPrice)}
                          </div>

                          {optionTotal > 0 ? (
                            <div className="text-primary small">
                              옵션 +{formatPrice(optionTotal)}
                            </div>
                          ) : null}

                          <div className="text-muted small">
                            단가 {formatPrice(unitPrice)} · 수량 {quantity}
                          </div>
                        </div>
                      </div>

                      <div className="fw-bold">
                        {formatPrice(resultPrice)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body d-flex justify-content-between">
                <div className="fw-bold">총 결제금액</div>
                <div className="fw-bold">{formatPrice(totalPrice)}</div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="card">
              <div className="card-body">
                <div className="fw-bold mb-2">주문자 정보</div>

                <div className="mb-2">
                  <label className="form-label small text-muted">이름 (필수)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label small text-muted">전화번호 (필수)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="sticky-bar p-3 pt-0">
                <button
                  type="submit"
                  className="btn btn-primary w-100 py-3 fw-bold"
                  disabled={submitting}
                >
                  {submitting
                    ? "처리 중..."
                    : `결제수단 선택하기 (${Number(totalPrice || 0).toLocaleString("ko-KR")}원)`}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <div className="fw-bold">결제 금액</div>
                <div className="fs-4 fw-bold">{formatPrice(totalPrice)}</div>
                <div className="text-muted small mt-1">주문명: {ordername}</div>
                <div className="text-muted small">주문번호: {orderId}</div>
              </div>
            </div>

            <div className="card p-3 mb-3">
              <div id="payment-method"></div>
              <button
                type="button"
                className="btn btn-primary w-100 py-3 fw-bold mt-3"
                onClick={requestPayment}
              >
                결제하기
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MobileCheckout;